// Extracted here to avoid a component → context import cycle via MicTestPreview.

export type MicTestPhase = 'recording' | 'generating' | 'finished' | 'editing';

export interface EditActivity {
  updatedStyle: boolean;
  usedEvidence: boolean;
  createdDocument: boolean;
}

/**
 * Whether the active display locale is English-based (e.g. `en`, `en-GB`,
 * `en-AU`). Non-English markets hide the "Update note" (style + abbreviations)
 * editing task, since the abbreviation swap is English-only. Undefined defaults
 * to English so existing behavior is preserved.
 */
export function isEnglishLocale(locale?: string): boolean {
  return (locale ?? 'en').toLowerCase().startsWith('en');
}

/** Learn-the-basics edit tasks in reveal order (checklist + legacy preview). */
export const EDIT_TASK_ORDER: (keyof EditActivity)[] = [
  'updatedStyle',
  'usedEvidence',
  'createdDocument',
];

/** Task keys shown for the active locale (drops `updatedStyle` outside English). */
export function getActiveEditTaskKeys(locale?: string): (keyof EditActivity)[] {
  if (isEnglishLocale(locale)) return EDIT_TASK_ORDER;
  return EDIT_TASK_ORDER.filter((key) => key !== 'updatedStyle');
}

/** First incomplete edit task for the locale, or `null` when all are done. */
export function getCurrentEditTask(
  editActivity: EditActivity,
  locale?: string
): keyof EditActivity | null {
  return getActiveEditTaskKeys(locale).find((key) => !editActivity[key]) ?? null;
}

/**
 * Verbosity level for the Learn-the-basics "Choose a detail level" control.
 * Applied as a client-side transform of the parsed note (see
 * `applyDetailLevel` in MicTestPreview) — there are no server-side variants.
 */
export type DetailLevel = 'low' | 'medium' | 'high';

/**
 * Tabs shown above the note preview during the Learn-the-basics flow.
 * `doc-${index}` identifies a per-document tab (multi-select Documents step),
 * keyed by the document option index. `transcript` only exists in the
 * guided-tour arm, whose tab strip keeps the source transcript reachable
 * alongside everything generated from it.
 */
export type PreviewTab = 'note' | 'evidence' | 'transcript' | `doc-${number}`;
